import React, { useEffect, useState } from 'react';

import ModuloItemSection from './ModuloItemSection';
import BoxConteudo from '../../../../../../../components/BoxConteudo';
import CustomButton from '../../../../../../../components/CustomButton';
import CustomCol from '../../../../../../../components/CustomCol';
import CustomInputText from '../../../../../../../components/CustomInputs/CustomInputText';
import CustomOffcanvas from '../../../../../../../components/CustomOffcanvas';
import CustomRow from '../../../../../../../components/CustomRow';
import CustomSelect from '../../../../../../../components/CustomSelects/CustomSelect';
import Log from '../../../../../../../components/Log/Log';
import {
  IModuloSubmoduloForm,
  IModuloSubmoduloRegistro,
} from '../../../../../../../modules/types/ModuloSisaf';
import theme from '../../../../../../../theme';
import { formataNomeMeioSiglas } from '../../../../../../../utils/functions';
import { handleSave } from '../../../../../../../utils/handleSave';
import { optionStatusBin } from '../../../../Dominios/utils/optionStatusBin';

interface ModuloSubmoduloFormProps {
  moduloId: number;
  registro: IModuloSubmoduloRegistro | null;
  onClose: () => void;
  onSaved: (savedForm: IModuloSubmoduloForm) => void;
  onDeleted: (id: number) => void;
  onRefresh?: () => void;
  onLogsRefresh?: () => void;
}

const ModuloSubmoduloForm: React.FC<ModuloSubmoduloFormProps> = ({
  moduloId,
  registro,
  onClose,
  onSaved,
  onDeleted,
  onRefresh,
  onLogsRefresh,
}) => {
  const [logRefresh, setLogRefresh] = useState(0);
  const camposObrigatorios = {
    status: 'Status',
    submodulo: 'Nome do Submódulo',
  };

  const camposAjustar = {
    number: ['id', 'moduloId', 'status'],
    string: ['submodulo', 'descricao'],
  };

  const [form, setForm] = useState<IModuloSubmoduloForm>({
    id: 0,
    moduloId,
    status: 0,
    submodulo: '',
    descricao: '',
  });

  useEffect(() => {
    if (registro) {
      setForm({
        id: registro.id,
        moduloId: registro.modulo?.id || moduloId,
        status: registro.status,
        submodulo: registro.submodulo,
        descricao: registro.descricao || '',
      });
      return;
    }

    setForm({
      id: 0,
      moduloId,
      status: 0,
      submodulo: '',
      descricao: '',
    });
  }, [registro, moduloId]);

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;
    const parsedValue = name === 'status' ? Number(value) : value;
    setForm((prev) => ({ ...prev, [name]: parsedValue }));
  };

  const handleSubmit = async () => {
    const saved = await handleSave(
      { ...form, moduloId },
      camposObrigatorios,
      camposAjustar,
      'modulossubmodulos',
      'daf/adm/modulossiaf/modulo',
      undefined,
      false,
    );

    if (saved) {
      onSaved({ ...form, moduloId });
      onLogsRefresh?.();
      setLogRefresh((prev) => prev + 1);
    }
  };

  return (
    <CustomOffcanvas
      show
      onClose={onClose}
      title={form.id ? 'Editar Submódulo' : 'Novo Submódulo'}
      width="50%"
      showOpenButton={false}
    >
      {registro && registro.id !== 0 && (
        <div style={{ marginBottom: '12px' }}>
          <Log
            id={registro.id}
            dataRegistro={registro.dataRegistro}
            usuarioRegistro={formataNomeMeioSiglas(
              registro.usuarioRegistro?.nomeCompleto,
            )}
            dataAtualizacao={registro.dataUltimaAtualizacao}
            usuarioAtualizacao={formataNomeMeioSiglas(
              registro.usuarioAtualizacao?.nomeCompleto,
            )}
            nEdicoes={registro.nEdicoes}
            entity="TB_MODULO_SUBMODULO"
            refreshTriggerLog={logRefresh}
          />
        </div>
      )}

      <BoxConteudo boxTitulo="Submódulo">
        <CustomRow>
          <CustomCol width="200px">
            <CustomSelect
              name="status"
              options={optionStatusBin}
              title="Status"
              value={form.status}
              onChange={handleChange}
              style={{
                backgroundColor:
                  Number(form.status) === 2
                    ? theme.colors.vermelho
                    : Number(form.status) === 1
                      ? theme.colors.verde
                      : 'white',
                color: Number(form.status) === 0 ? '#000000' : '#ffffff',
              }}
              required
            />
          </CustomCol>
          <CustomCol>
            <CustomInputText
              id="submodulo"
              name="submodulo"
              value={form.submodulo}
              title="Nome do Submódulo"
              onChange={handleChange}
              required
            />
          </CustomCol>
        </CustomRow>
        <CustomRow>
          <CustomCol>
            <CustomInputText
              id="descricaoSubmodulo"
              name="descricao"
              value={form.descricao || ''}
              title="Descrição"
              type="textArea"
              rows={3}
              onChange={handleChange}
            />
          </CustomCol>
        </CustomRow>

        <CustomRow
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'nowrap',
            marginTop: '16px',
          }}
        >
          {form.id !== 0 && (
            <CustomButton
              label="Deletar"
              bgColor={theme.colors.vermelho}
              textColor={theme.colors.white}
              style={{ minWidth: '110px', width: '120px' }}
              onClick={() => onDeleted(form.id)}
            />
          )}
          <CustomButton
            label="Salvar"
            bgColor={theme.colors.botaoAcao.azul}
            textColor={theme.colors.white}
            style={{ minWidth: '110px', width: '120px' }}
            onClick={handleSubmit}
          />
        </CustomRow>
      </BoxConteudo>

      {registro && registro.id !== 0 && (
        <ModuloItemSection
          submodulo={registro}
          onUpdated={() => {
            (onRefresh || (() => null))();
            onLogsRefresh?.();
            setLogRefresh((prev) => prev + 1);
          }}
        />
      )}
    </CustomOffcanvas>
  );
};

export default ModuloSubmoduloForm;
